import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import {
  Input,
  Select,
  Checkbox,
  RadioButton,
  FieldTable,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  handleAddFieldTableRow,
  handleEditFieldTableRow,
  handleSaveFieldTableRow,
  handleDeleteFieldTableRow,
  handleFieldTableChange,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";

function SCTFCFollowUp() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <Input
                    name="Medicaid"
                    label="Medicaid #:"
                    error={errors.Medicaid}
                    value={data.Medicaid["@value"]}
                    onChange={handleChange}
                    identical={compare("Medicaid")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Input
                    name="FollowUpDate"
                    label="Date of follow up assessment:"
                    error={errors.FollowUpDate}
                    value={data.FollowUpDate["@value"]}
                    onChange={handleChange}
                    identical={compare("FollowUpDate")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                  />
                </div>
              </div>
              <div className="h6">Current Diagnosis - Full</div>
              <FieldTable
                name="CurrentDiagnosis"
                commentsHandler={handleComments}
              >
                <div className="container border-top border-start border-end mb-2">
                  <div className="row border-bottom">
                    <div className="col border-end py-2">Date</div>
                    <div className="col border-end py-2">Diagnosis</div>
                    <div className="col py-2">
                      Clinical Professional
                      <br />
                      (Name and Credentials)
                    </div>
                  </div>
                  {data.CurrentDiagnosis["@value"] &&
                    JSON.parse(data.CurrentDiagnosis["@value"]).map(
                      (item, index) => (
                        <div key={item._id} className="row border-bottom">
                          <div className="col border-end py-2 d-flex">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <>
                                <i
                                  className="fas fa-save me-2 d-print-none"
                                  onClick={() => {
                                    handleSaveRow(index, "CurrentDiagnosis");
                                  }}
                                ></i>
                                <i
                                  className="far fa-trash-alt me-2 d-print-none"
                                  onClick={() => {
                                    handleDeleteRow(index, "CurrentDiagnosis");
                                  }}
                                ></i>
                                <Input
                                  name="Date"
                                  value={item.Date}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "CurrentDiagnosis"
                                    );
                                  }}
                                  disabled={formDisabled}
                                  type="date"
                                />
                              </>
                            ) : (
                              <>
                                {!formDisabled && (
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(index, "CurrentDiagnosis");
                                    }}
                                  ></i>
                                )}
                                {item.Date !== undefined && item.Date !== ""
                                  ? moment(item.Date).format("l")
                                  : null}
                              </>
                            )}
                          </div>
                          <div className="col border-end py-2">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <Input
                                name="Diagnosis"
                                value={item.Diagnosis}
                                onChange={(e) => {
                                  handleRowChange(e, index, "CurrentDiagnosis");
                                }}
                                disabled={formDisabled}
                              />
                            ) : (
                              item.Diagnosis
                            )}
                          </div>
                          <div className="col py-2">
                            {item.IsEditable === undefined ||
                            item.IsEditable ? (
                              <Input
                                name="ClinicalProfessional"
                                value={item.ClinicalProfessional}
                                onChange={(e) => {
                                  handleRowChange(e, index, "CurrentDiagnosis");
                                }}
                                disabled={formDisabled}
                              />
                            ) : (
                              item.ClinicalProfessional
                            )}
                          </div>
                        </div>
                      )
                    )}
                </div>
                {!formDisabled ? (
                  <button
                    className="btn btn-primary mb-2"
                    onClick={() => {
                      handleAddRow("CurrentDiagnosis", { IsEditable: true });
                    }}
                  >
                    Add Diagnosis
                  </button>
                ) : null}
                {errors.CurrentDiagnosis && (
                  <div className="alert alert-danger">
                    {errors.CurrentDiagnosis}
                  </div>
                )}
              </FieldTable>
              <br />
              <b>Diagnostic Impressions</b>
              <Input
                name="BehaviorChanges"
                label="Significant changes in behavior and/or condition:"
                error={errors.BehaviorChanges}
                value={data.BehaviorChanges["@value"]}
                onChange={handleChange}
                identical={compare("BehaviorChanges")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="TreatmentProgress"
                label="Response/Progress/Need for continued treatment:"
                error={errors.TreatmentProgress}
                value={data.TreatmentProgress["@value"]}
                onChange={handleChange}
                identical={compare("TreatmentProgress")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="ReferralsNeeded"
                label="Referrals needed:"
                error={errors.ReferralsNeeded}
                value={data.ReferralsNeeded["@value"]}
                options={yesNoOptions}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.ReferralsNeeded["@value"] !== "No" ? (
                <Input
                  name="ReferralsNeededDetails"
                  label="Describe:"
                  error={errors.ReferralsNeededDetails}
                  value={data.ReferralsNeededDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ReferralsNeededDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <div className="h6">Statement of Medical Necessity</div>
              <Input
                name="FollowUpCompletedBy"
                label="Follow up completed by:"
                error={errors.FollowUpCompletedBy}
                value={data.FollowUpCompletedBy["@value"]}
                onChange={handleChange}
                identical={compare("FollowUpCompletedBy")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <p>
                I,{" "}
                {data.FollowUpCompletedBy["@value"] !== undefined
                  ? data.FollowUpCompletedBy["@value"]
                  : null}
                , certify based on the findings of this Diagnostic Assessment,
                medical necessity is met for the following services to meet the
                needs of the identified beneficiary. In order to support the
                reduction of emotional, behavioral, and psychological distress
                and restoration of the beneficiary to his or her best possibly
                function, I recommend the receipt of rehabilitative services. As
                evidenced by the above diagnosis, noted in the DSMV and ICD10,
                this beneficiary meets the following medical necessity criteria.
              </p>
              <br />
              <div className="h6">Core Services:</div>
              <div className="ms-3">
                Details: Interference with at least two or more of the
                following:
                <div className="ms-3">
                  <Checkbox
                    name="CoreDailyLivingTasks"
                    label="Tasks of Daily Living"
                    checked={data.CoreDailyLivingTasks["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                  <Checkbox
                    name="CorePersonalRelationships"
                    label="Personal Relationships"
                    checked={
                      data.CorePersonalRelationships["@value"] === "true"
                    }
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                  <Checkbox
                    name="CoreWork"
                    label="Work/School"
                    checked={data.CoreWork["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                  <Checkbox
                    name="CoreRecreation"
                    label="Recreation"
                    checked={data.CoreRecreation["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
                <br />
                Recommendations
                <br />
                <div className="container border">
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">Service</div>
                    <div className="col py-2 border-end"># of Units</div>
                    <div className="col py-2">
                      Frequency (per day/per week/per month)
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="NoServices"
                        label="No Core Services Recommended"
                        checked={data.NoServices["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end"></div>
                    <div className="col py-2"></div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="IndividualTherapy"
                        label="Individual Therapy"
                        checked={data.IndividualTherapy["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="IndividualTherapyUnits"
                        error={errors.IndividualTherapyUnits}
                        value={data.IndividualTherapyUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("IndividualTherapyUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.IndividualTherapy["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="IndividualTherapyFrequency"
                        error={errors.IndividualTherapyFrequency}
                        value={data.IndividualTherapyFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("IndividualTherapyFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.IndividualTherapy["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="FamilyTherapy"
                        label="Family Therapy with Beneficiary"
                        checked={data.FamilyTherapy["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="FamilyTherapyUnits"
                        error={errors.FamilyTherapyUnits}
                        value={data.FamilyTherapyUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("FamilyTherapyUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.FamilyTherapy["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="FamilyTherapyFrequency"
                        error={errors.FamilyTherapyFrequency}
                        value={data.FamilyTherapyFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("FamilyTherapyFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.FamilyTherapy["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="WithoutBeneficiary"
                        label="Family Therapy without Beneficiary"
                        checked={
                          data.WithoutBeneficiary &&
                          data.WithoutBeneficiary["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="WithoutBeneficiaryUnits"
                        error={errors.WithoutBeneficiaryUnits}
                        value={
                          data.WithoutBeneficiaryUnits &&
                          data.WithoutBeneficiaryUnits["@value"]
                        }
                        onChange={handleChange}
                        identical={compare("WithoutBeneficiaryUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          !data.WithoutBeneficiary ||
                          data.WithoutBeneficiary["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="WithoutBeneficiaryFrequency"
                        error={errors.WithoutBeneficiaryFrequency}
                        value={
                          data.WithoutBeneficiaryFrequency &&
                          data.WithoutBeneficiaryFrequency["@value"]
                        }
                        onChange={handleChange}
                        identical={compare("WithoutBeneficiaryFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          !data.WithoutBeneficiary ||
                          data.WithoutBeneficiary["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="CrisisManagement"
                        label="Crisis Management (CM)"
                        checked={data.CrisisManagement["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="CrisisManagementUnits"
                        error={errors.CrisisManagementUnits}
                        value={data.CrisisManagementUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("CrisisManagementUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.CrisisManagement["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="CrisisManagementFrequency"
                        error={errors.CrisisManagementFrequency}
                        value={data.CrisisManagementFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("CrisisManagementFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.CrisisManagement["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="ServicePlan"
                        label="Service Plan Development (SPD)"
                        checked={data.ServicePlan["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="ServicePlanUnits"
                        error={errors.ServicePlanUnits}
                        value={data.ServicePlanUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("ServicePlanUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled || data.ServicePlan["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="ServicePlanFrequency"
                        error={errors.ServicePlanFrequency}
                        value={data.ServicePlanFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("ServicePlanFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled || data.ServicePlan["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <br />
              <div className="h6">Community Support Services:</div>
              <div className="ms-3">
                Details: Moderate to severe functioning that interferes with at
                least three or more of the following:
                <div className="ms-3">
                  <Checkbox
                    name="CommunityDailyLivingTasks"
                    label="Tasks of Daily Living"
                    checked={
                      data.CommunityDailyLivingTasks["@value"] === "true"
                    }
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                  <Checkbox
                    name="CommunityPersonalRelationships"
                    label="Personal Relationships"
                    checked={
                      data.CommunityPersonalRelationships["@value"] === "true"
                    }
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                  <Checkbox
                    name="CommunityWork"
                    label="Work/School"
                    checked={data.CommunityWork["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                  <Checkbox
                    name="CommunityRecreation"
                    label="Recreation"
                    checked={data.CommunityRecreation["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
                <div>
                  In addition:
                  <br />
                  <ul>
                    <li>
                      Beneficiary is expected to benefit from the intervention
                      and needs would not be better met by any other formal or
                      informal system or support.
                    </li>
                    <li>
                      Family/caregiver agrees to be an active participant in
                      treatment.
                    </li>
                  </ul>
                </div>
                <br />
                Recommendations
                <br />
                <div className="container border">
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">Service</div>
                    <div className="col py-2 border-end"># of Units</div>
                    <div className="col py-2">
                      Frequency (per day/per week/per month)
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="NoCommunitySupport"
                        label="No RBHS Recommended"
                        checked={data.NoCommunitySupport["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end"></div>
                    <div className="col py-2"></div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="Psychosocial"
                        label="Psychosocial Rehabilitative Service (PRS)"
                        checked={data.Psychosocial["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="PsychosocialUnits"
                        error={errors.PsychosocialUnits}
                        value={data.PsychosocialUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("PsychosocialUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled || data.Psychosocial["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="PsychosocialFrequency"
                        error={errors.PsychosocialFrequency}
                        value={data.PsychosocialFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("PsychosocialFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled || data.Psychosocial["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="FamilySupport"
                        label="Family Support (FS)"
                        checked={data.FamilySupport["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="FamilySupportUnits"
                        error={errors.FamilySupportUnits}
                        value={data.FamilySupportUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("FamilySupportUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.FamilySupport["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="FamilySupportFrequency"
                        error={errors.FamilySupportFrequency}
                        value={data.FamilySupportFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("FamilySupportFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.FamilySupport["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="BehaviorModification"
                        label="Behavior Modification (B-Mod)"
                        checked={data.BehaviorModification["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="BehaviorModificationUnits"
                        error={errors.BehaviorModificationUnits}
                        value={data.BehaviorModificationUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("BehaviorModificationUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.BehaviorModification["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="BehaviorModificationFrequency"
                        error={errors.BehaviorModificationFrequency}
                        value={data.BehaviorModificationFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("BehaviorModificationFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.BehaviorModification["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="CommunityCrisisManagement"
                        label="Crisis Management (CM)"
                        checked={
                          data.CommunityCrisisManagement["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="CommunityCrisisManagementUnits"
                        error={errors.CommunityCrisisManagementUnits}
                        value={data.CommunityCrisisManagementUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("CommunityCrisisManagementUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.CommunityCrisisManagement["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="CommunityCrisisManagementFrequency"
                        error={errors.CommunityCrisisManagementFrequency}
                        value={
                          data.CommunityCrisisManagementFrequency["@value"]
                        }
                        onChange={handleChange}
                        identical={compare(
                          "CommunityCrisisManagementFrequency"
                        )}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.CommunityCrisisManagement["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="CommunityServicePlan"
                        label="Service Plan Development (SPD)"
                        checked={data.CommunityServicePlan["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="CommunityServicePlanUnits"
                        error={errors.CommunityServicePlanUnits}
                        value={data.CommunityServicePlanUnits["@value"]}
                        onChange={handleChange}
                        identical={compare("CommunityServicePlanUnits")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.CommunityServicePlan["@value"] !== "true"
                        }
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="CommunityServicePlanFrequency"
                        error={errors.CommunityServicePlanFrequency}
                        value={data.CommunityServicePlanFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("CommunityServicePlanFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          data.CommunityServicePlan["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col py-2 border-end">
                      <Checkbox
                        name="FosterCare"
                        label="Therapeutic Foster Care (TFC)"
                        checked={data.FosterCare["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col py-2 border-end">No units required</div>
                    <div className="col py-2">
                      <Input
                        name="FosterCareFrequency"
                        error={errors.FosterCareFrequency}
                        value={data.FosterCareFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("FosterCareFrequency")}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled || data.FosterCare["@value"] !== "true"
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SCTFCFollowUp;
